// Activate dialog (spec 01 §4 S4 "Activate dialog as S3"; §3.5). Collects the
// effective date (today or future — the server rejects back-dating with
// EFFECTIVE_DATE_PAST) and the required activation note, and previews the §3.5
// activation warnings before the human confirms:
//   • no steps         → blocks activation (server ROUTING_NO_STEPS)
//   • consume mismatch → non-blocking warning (BOM/routing version independently)
//   • active version   → "vN will be superseded" preview.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Banner } from "../../../../../components/toast";

export interface ActivateWarnings {
  noSteps: boolean;
  missingConsumeSteps: number[];
  activeVersion: number | null;
  unsaved: boolean;
}

export function ActivateDialog({
  todayIso,
  warnings,
  pending,
  errorMessage,
  requestId,
  onConfirm,
  onCancel,
}: {
  todayIso: string;
  warnings: ActivateWarnings;
  pending: boolean;
  errorMessage: string | null;
  requestId: string | undefined;
  onConfirm: (input: { effective_from: string; activation_note: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso);
  const [note, setNote] = useState("");

  const blocked = warnings.noSteps || warnings.unsaved;
  const canConfirm = !pending && !blocked && note.trim().length > 0 && effectiveFrom.length > 0;

  return (
    <div className="cat-scrim cat-scrim-center" role="presentation" onClick={onCancel}>
      <div
        className="cat-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("catalogue.routing.activate.title")}
        data-testid="activate-dialog"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <header className="cat-modal-head">
          <h2>{t("catalogue.routing.activate.title")}</h2>
        </header>

        <div className="cat-modal-body">
          {warnings.unsaved ? (
            <Banner kind="bad">{t("catalogue.routing.activate.unsavedWarning")}</Banner>
          ) : null}
          {warnings.noSteps ? (
            <Banner kind="bad">{t("catalogue.routing.activate.noStepsWarning")}</Banner>
          ) : null}
          {warnings.missingConsumeSteps.length > 0 ? (
            <Banner kind="warn">
              {t("catalogue.routing.activate.consumeMismatchWarning", {
                steps: warnings.missingConsumeSteps.join(", "),
              })}
            </Banner>
          ) : null}
          {warnings.activeVersion !== null ? (
            <Banner kind="neutral">
              {t("catalogue.routing.activate.supersedePreview", {
                version: warnings.activeVersion,
              })}
            </Banner>
          ) : null}

          <label className="cat-fld">
            <span>{t("catalogue.routing.activate.effectiveFrom")}</span>
            <input
              type="date"
              min={todayIso}
              value={effectiveFrom}
              onChange={(e) => {
                setEffectiveFrom(e.target.value);
              }}
            />
            <span className="cat-hint">{t("catalogue.routing.activate.effectiveFromHint")}</span>
          </label>

          <label className="cat-fld">
            <span>{t("catalogue.routing.activate.note")}</span>
            <textarea
              rows={3}
              value={note}
              placeholder={t("catalogue.routing.activate.notePlaceholder")}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </label>

          {errorMessage !== null ? (
            <Banner kind="bad">
              {errorMessage}
              {requestId !== undefined && requestId.length > 0 ? (
                <span className="rte-request-id">
                  {" "}
                  {t("catalogue.state.requestId")}: <code>{requestId}</code>
                </span>
              ) : null}
            </Banner>
          ) : null}
        </div>

        <footer className="cat-modal-foot">
          <div className="cat-modal-foot-right">
            <Button onClick={onCancel} disabled={pending}>
              {t("catalogue.routing.activate.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!canConfirm}
              busy={pending}
              busyLabel={t("catalogue.routing.activate.activating")}
              onClick={() => {
                onConfirm({ effective_from: effectiveFrom, activation_note: note.trim() });
              }}
            >
              {t("catalogue.routing.activate.confirm")}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
